/**
 * Moments of the wedding day (Cotiza v2). A moment groups the default
 * event-day timeline steps; the client picks one space per moment.
 * Spec §5.4.
 */
export const MOMENT_IDS = [
    'bienvenida',
    'ceremonia',
    'coctel',
    'cena',
    'fiesta',
    'trasnocho',
];
export const MOMENTS = [
    { id: 'bienvenida', name: 'Bienvenida', stepNames: ['Llegada de invitados'] },
    { id: 'ceremonia', name: 'Ceremonia', stepNames: ['Celebración espiritual'] },
    {
        id: 'coctel',
        name: 'Pasabocas',
        stepNames: ['Pasabocas/Cocteles', 'Fotografía invitados', 'Brindis'],
    },
    { id: 'cena', name: 'Cena', stepNames: ['Cena', 'Torta'] },
    {
        id: 'fiesta',
        name: 'Fiesta',
        stepNames: [
            'Primer Baile',
            'Fiesta - actividades (ramo, liga, etc)',
            'Fiesta - Tambores y Robot',
            'Hora Loca',
            'Fiesta',
        ],
    },
    { id: 'trasnocho', name: 'Trasnocho', stepNames: ['Caldo', 'Cierre del evento'] },
];
/** Lowercase, accent-free, any dash style -> '-', single spaces. */
export function normalizeStepName(name) {
    return name
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[‐-―−]/g, '-')
        .replace(/\s*-\s*/g, ' - ')
        .replace(/\s+/g, ' ')
        .trim();
}
const STEP_NAME_TO_MOMENT = new Map(MOMENTS.flatMap((m) => m.stepNames.map((n) => [normalizeStepName(n), m.id])));
/** Moment a timeline step belongs to, or null (unmatched / renamed steps). */
export function momentOf(activityName) {
    if (!activityName)
        return null;
    const key = normalizeStepName(activityName);
    const exact = STEP_NAME_TO_MOMENT.get(key);
    if (exact)
        return exact;
    if (key.startsWith('fiesta - '))
        return 'fiesta';
    return null;
}
/** Moment of a step: its stored override (`null` = standalone) or, when absent, the default from its name. */
export function stepMoment(step) {
    if (step.moment_id === null)
        return null;
    if (step.moment_id && MOMENT_IDS.includes(step.moment_id)) {
        return step.moment_id;
    }
    return momentOf(step.activity_name);
}
/**
 * Steps that define their moment (the moment is the step): they cannot be moved to another moment, which is what
 * keeps e.g. "Cena" out of Pasabocas. Every other step may be assigned to any moment, time order permitting.
 */
export const ANCHOR_STEP_NAMES = [
    'Llegada de invitados',
    'Celebración espiritual',
    'Pasabocas/Cocteles',
    'Cena',
    'Fiesta',
    'Caldo',
    'Cierre del evento',
];
export function isAnchorStep(activityName) {
    const key = normalizeStepName(activityName);
    return ANCHOR_STEP_NAMES.some((n) => normalizeStepName(n) === key);
}
/** Wire value of `moment_id` that clears a step's override (back to the default from its name). */
export const MOMENT_AUTO = 'auto';
/**
 * Moments `step` may belong to (event day only; `others` = the rest of the timeline). Anchor steps only keep their
 * own moment. Any other step may join a moment unless that would break the moments' time order (no step of an
 * earlier moment may start after it, and none of a later moment before it) or split the moment on the rail, which
 * only groups consecutive rows (no other step may start between it and the moment's other steps). `null`
 * (standalone) is always valid for non-anchor steps.
 */
export function allowedMomentsForStep(step, others) {
    if (isAnchorStep(step.activity_name)) {
        const own = momentOf(step.activity_name);
        return own ? [own] : [];
    }
    const live = others.filter((o) => o.day_number === 0 && o.is_active !== false && !o.is_orphaned);
    const between = (x, a, b) => Math.min(a, b) < x && x < Math.max(a, b);
    return MOMENT_IDS.filter((id, i) => {
        const members = live.filter((o) => stepMoment(o) === id);
        return live.every((o) => {
            const oi = MOMENT_IDS.indexOf(stepMoment(o));
            if (oi === i)
                return true;
            if (oi >= 0 && (oi < i ? o.start_minute > step.start_minute : o.start_minute < step.start_minute)) {
                return false;
            }
            return !members.some((m) => between(o.start_minute, step.start_minute, m.start_minute));
        });
    });
}
/**
 * Clears the overrides that no longer hold: off the event day, on anchor steps, or no longer allowed after other
 * steps moved. Repeats until stable, since clearing one changes what the others may join. Mutates `steps`.
 */
export function pruneMomentOverrides(steps) {
    const stale = (s) => {
        if (s.moment_id === undefined)
            return false;
        if (s.day_number !== 0 || isAnchorStep(s.activity_name))
            return true;
        if (s.moment_id === null)
            return false;
        const others = steps.filter((o) => o !== s);
        return !allowedMomentsForStep(s, others).includes(s.moment_id);
    };
    for (let s = steps.find(stale); s; s = steps.find(stale)) {
        s.moment_id = undefined;
    }
}
/** Main-house sub-spaces the v2 client UI can assign to a moment. */
export const MAIN_HOUSE_SPACE_CODES = [
    'casa',
    'deck',
    'buda',
    'lago',
    'pergola_principal',
    'agora_fuego',
];
export function isMainHouseSpace(code) {
    return MAIN_HOUSE_SPACE_CODES.includes(code);
}
/**
 * Space codes a moment may use. Main-house sub-space codes plus Location
 * `space_code`s (`jardin_puertas`, `salon_bosque`, `experiencia`).
 */
export const MOMENT_ALLOWED_SPACES = {
    bienvenida: ['pergola_principal', 'lago', 'buda'],
    ceremonia: ['deck', 'jardin_puertas', 'salon_bosque', 'pergola_principal', 'buda'],
    coctel: ['buda', 'lago', 'jardin_puertas', 'salon_bosque', 'deck'],
    cena: ['buda', 'salon_bosque', 'jardin_puertas'],
    fiesta: ['buda', 'salon_bosque'],
    trasnocho: ['agora_fuego', 'casa', 'salon_bosque'],
};
/**
 * Consecutive moments (by `MOMENT_IDS` order) that may happen in the same space. Every other consecutive pair needs
 * different spaces: the space is set up for the next moment while guests are at the current one. Moments that are
 * not consecutive never conflict (guests have moved on, so a space can be reused later).
 */
export const MOMENT_CAN_SHARE_SPACE_WITH_NEXT = {
    cena: true,
    fiesta: true,
};
/**
 * Consecutive moments of `momentId` (previous and next) that already use one of `spaces` and may not share it.
 * `spacesByMoment` holds the spaces each moment is on today (a moment with none picked is absent or empty).
 */
export function momentsSharingSpace(momentId, space, spacesByMoment) {
    const i = MOMENT_IDS.indexOf(momentId);
    const neighbours = [
        [MOMENT_IDS[i - 1], !!MOMENT_CAN_SHARE_SPACE_WITH_NEXT[MOMENT_IDS[i - 1]]],
        [MOMENT_IDS[i + 1], !!MOMENT_CAN_SHARE_SPACE_WITH_NEXT[momentId]],
    ];
    return neighbours.flatMap(([id, canShare]) => id && !canShare && spacesByMoment[id]?.includes(space) ? [id] : []);
}
/** Every space code a service may be tagged with (main-house + location spaces). */
export const ALL_SPACE_CODES = Array.from(new Set([
    ...MAIN_HOUSE_SPACE_CODES,
    // Still a valid service tag although no moment offers it any more
    'experiencia',
    ...Object.values(MOMENT_ALLOWED_SPACES).flat(),
]));
/**
 * Photos per main-house sub-space (static; no runtime DB read), all on the Spaces
 * CDN (`images/locations/`). The first photo is the one shown on tiles.
 */
export const TIMELINE_LOCATION_MEDIA = {
    casa: [
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1793152825129-Sala_piso_1_casa_aragon_hospedaje_bodas_matrimonios-2-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1793152826513-Sala_piso_1_casa_aragon_hospedaje_bodas_matrimonios-3-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1793152827909-Sala_piso_1_casa_aragon_hospedaje_bodas_matrimonios-11-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1793152829308-zona_estar_piso_2_casa_aragon_hospedaje-9-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1793152830294-zona_estar_piso_2_casa_aragon_hospedaje-11-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1793152831189-zona_estar_piso_2_casa_aragon_hospedaje-12-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1793152832187-Comedor_casa_aragon_bodas_matrimonios-15-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1793152833358-Comedor_casa_aragon_bodas_matrimonios-7-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1793152834273-cocina_casa_aragon_cena_matrimonios_bodas-3-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1793152835119-cocina_casa_aragon_cena_matrimonios_bodas-10-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1793152836283-Corredores_accesos_casa_aragon_bodas_matrimonios-6-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1793152837230-Jacuzzi_casa_aragon_bodas_matrimonios-9-1600.webp',
    ],
    pergola_principal: [
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1778906199646-Pergola-principal-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1789072650971-Pergola-de-entrada1-1600.webp',
    ],
    buda: [
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1791762991020-buda-1-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1791762991021-buda-2-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1791762991022-buda-3-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1791762991023-buda-4-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1791762991024-buda-5-1600.webp',
    ],
    lago: [
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1791762991015-lago-1-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1791762991016-lago-2-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1791762991017-lago-3-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1791762991018-lago-4-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1791762991019-lago-5-1600.webp',
    ],
    deck: [
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1791762991025-deck-1-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1791762991026-deck-2-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1791762991027-deck-3-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1791762991028-deck-4-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1791762991029-deck-5-1600.webp',
    ],
    agora_fuego: [
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1791762991030-agora-fuego-1-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1791762991031-agora-fuego-2-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1791762991032-agora-fuego-3-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1791762991033-agora-fuego-4-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1791762991034-agora-fuego-5-1600.webp',
    ],
};
/** The two indoor salones of the Casa Principal (shown in the Interior de la casa sheet; not moment spaces). */
export const SALON_CODES = ['urapan', 'cedro'];
/** Bundled photos of the salones, on the Spaces CDN like `TIMELINE_LOCATION_MEDIA`. The first is the tile photo. */
export const SALON_MEDIA = {
    urapan: [
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1793154241380-Urapan-ceremonia-sala-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1793154242782-Urapan-ceremonia-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1793154239745-Urapan-cena-1600.webp',
    ],
    cedro: [
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1793154234212-Cedro-DSC_1250-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1793154232854-Cedro-DSC_1249-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1793154231361-Cedro-DSC_1248-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1793154237873-Cedro-IMG_2619-1600.webp',
        'https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/locations/1793154235756-Cedro-IMG_2615-1600.webp',
    ],
};
/** Codes the Casa Principal keeps photos for in `space_images`: its included sub-spaces and its salones. */
export const HOUSE_PHOTO_CODES = [...MAIN_HOUSE_SPACE_CODES, ...SALON_CODES];
export function isHousePhotoCode(code) {
    return HOUSE_PHOTO_CODES.includes(code);
}
/** Bundled photos of a house photo code (sub-space or salon); empty for any other code. */
export function bundledSpaceMedia(code) {
    return TIMELINE_LOCATION_MEDIA[code] ?? SALON_MEDIA[code] ?? [];
}
/**
 * Photos of a main-house sub-space or salon: the ones admins uploaded on the Casa Principal (`space_images`), else the
 * bundled defaults.
 */
export function mainHouseSpaceMedia(code, spaceImages) {
    const uploaded = spaceImages?.[code];
    return uploaded?.length ? uploaded : bundledSpaceMedia(code);
}
/**
 * Whether a URL is one of the bundled `TIMELINE_LOCATION_MEDIA` / `SALON_MEDIA` photos. Those files back the fallback in every
 * environment, so they are never deleted from Spaces, even when an admin removes them from a gallery.
 */
export function isBundledSpaceMedia(url) {
    return [...Object.values(TIMELINE_LOCATION_MEDIA), ...Object.values(SALON_MEDIA)].some((urls) => urls.includes(url));
}
